import { SelectOption, useNotify } from '@globaleris/ui.library'
import { Button, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoSujeto } from '../../../api'
import { ConceptModifiedModel } from '../../../api/Argentia/endpoints/conceptsModified/model/conceptsModified'
import { ContractSummaryModel } from '../../../api/Argentia/endpoints/contracts/model/contracts'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import { ProductModel } from '../../../api/Argentia/model/contracts'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import SimpleDialog from '../../../components/shared/simpleDialog/simpleDialog'
import { getFilteredBreakdownOptions } from '../../../helpers/breakdownOptionsHelper'
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { ConceptModifiedCard } from '../../concepts/conceptModified/components/conceptModifiedCard'
import { ConceptModifiedEditForm } from '../../concepts/conceptModified/edit'
import {
  mapConceptModifiedModelToFormData,
  mapFormDataToConceptModifiedPatchRequest,
  mapFormDataToConceptPriceDiscriminantsPatch
} from '../../concepts/concepts.mapper'
import {
  ConceptModifiedFormEditModel,
  ConceptModifiedTableModel
} from '../../concepts/concepts.models'
import {
  deleteConceptModified,
  getConceptsModifiedByContractProductID,
  updateConceptModified,
  updateConceptPrice
} from '../../concepts/concepts.service'
import { ProductDetail } from '../edit/components/productDetail/productDetail'
import { ConceptModificationType } from '../enums/conceptModificationType.enum'
import { mapConcepts, mapProductModel } from '../products.mapper'
import {
  deleteConceptPrice,
  fetchProductById,
  fetchProductTypeOptionsByClientType,
  getProductConceptPricesByProductID
} from '../products.service'
import { styles } from './products.customize.styles'

interface CustomizeProductProps {
  location?: {
    state: {
      productContractId?: number // required for fetching concepts modified data (contract modification mode)
      productContractStartDate?: string
      productContractEndDate?: string
      productId: number // required for fetching product details
      clientType: EnumClientTypes | EnumTipoSujeto //required for fetching product details and product type options
      contractData?: ContractSummaryModel & { commodity: EnumCommodity } // optional to show in leftPanel Details (contract modification mode)
      conceptModificationType?: ConceptModificationType // Diferencia: tipo de personalización
      contractCode?: string // required for updating concepts modified
      contractProductCode?: string // required for updating concepts modified
    }
  }
}

export const CustomizeProductPage: React.FC<CustomizeProductProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'products', 'conceptModifiedCard'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const productContractId = props.location?.state?.productContractId
  const productId = props.location?.state?.productId
  const productContractStartDate = props.location?.state?.productContractStartDate
  const productContractEndDate = props.location?.state?.productContractEndDate
  const clientType = props.location?.state?.clientType
  const contractData = props.location?.state?.contractData
  const conceptModificationType = props.location?.state?.conceptModificationType

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [conceptsModifiedData, setConceptsModifiedData] = useState<
    ConceptModifiedModel[] | undefined
  >(undefined)
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState<boolean>(false)
  const [productDetail, setProductDetail] = useState<ProductModel | undefined>(undefined)
  const [productTypeOptions, setProductTypeOptions] = useState<SelectOption[]>([])
  const [breakdownOptions, setBreakdownOptions] = useState<SelectOption[]>([])
  const [formData, setFormData] = useState<ConceptModifiedFormEditModel>(
    {} as ConceptModifiedFormEditModel
  )
  const [formIsValid, setFormIsValid] = useState<boolean>(false)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const [editContractCode, setEditContractCode] = useState<string>('')
  const [editContractProductCode, setEditContractProductCode] = useState<string>('')

  const contractCode = props.location?.state?.contractCode || contractData?.code
  const contractProductCode = props.location?.state?.contractProductCode || productDetail?.code

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleOpenDialog = () => {
    history.push(NonAuthRoutes.ConceptsModifiedAdd, {
      productId: productId,
      contractCode: contractData?.code!,
      contractProductCode: productDetail?.code!,
      productContractStartDate: productContractStartDate,
      productContractEndDate: productContractEndDate,
      contractId: contractData?.id!,
      contractProductId: productContractId!,
      commodity:
        conceptModificationType === ConceptModificationType.PRODUCT
          ? productDetail?.commodity
          : contractData?.commodity,
      conceptModificationType: conceptModificationType
    })
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setHasChanges(false)
  }

  const handleEditConceptModified = (selectedRow: ConceptModifiedTableModel) => {
    // Find the full concept data from conceptsModifiedData
    const conceptToEdit = conceptsModifiedData?.find(cm => cm.id === selectedRow.id)

    if (conceptToEdit) {
      const mappedFormData = mapConceptModifiedModelToFormData(conceptToEdit)
      setFormData(mappedFormData)
      setFormIsValid(true) // In edit mode, data is initially valid
      setHasChanges(false) // No changes yet
      setEditContractCode(contractCode || '')
      setEditContractProductCode(contractProductCode || '')
      setDialogOpen(true)
    }
  }

  const handleSaveConceptModified = async () => {
    const request = mapFormDataToConceptModifiedPatchRequest(
      formData,
      editContractCode!,
      editContractProductCode!
    )
    let response = await updateConceptModified(request)

    // Los discriminantes de precio (región impuestos, peaje, etc.) de una personalización
    // a nivel producto viven en r_concepto_precio, no en ConceptModified, así que se
    // actualizan aparte contra el mismo endpoint que usa la edición del concepto base.
    if (
      response.success &&
      conceptModificationType === ConceptModificationType.PRODUCT &&
      formData.id
    ) {
      const discriminantsPatch = mapFormDataToConceptPriceDiscriminantsPatch(formData.id, formData)
      const discriminantsResponse = await updateConceptPrice(
        formData.masterConceptId,
        formData.id,
        discriminantsPatch,
        '2.0'
      )
      response = { ...response, success: response.success && discriminantsResponse.success }
    }

    if (response.success) {
      info(t('conceptModifiedCard:forms.edit.notifications.saveSuccess'))
      handleCloseDialog()
      await getConceptsModifiedData()
    } else {
      error(t('conceptModifiedCard:forms.edit.notifications.saveError'))
    }
  }

  const handleFormChange = (formState: ConceptModifiedFormEditModel, formIsCompleted: boolean) => {
    setFormData(formState)
    setFormIsValid(formIsCompleted)

    // Mark that changes were made
    if (!hasChanges) {
      setHasChanges(true)
    }
  }

  const handleDeleteConceptModified = (selectedRow: ConceptModifiedTableModel) => {
    getConfirmation(ConfirmationDialogs.DELETE_CONCEPT_MODIFIED).then(async () => {
      let response

      if (conceptModificationType === ConceptModificationType.PRODUCT) {
        response = await deleteConceptPrice(selectedRow.id)
      } else {
        response = await deleteConceptModified(selectedRow.id)
      }
      if (response.success) {
        info(t('conceptModifiedCard:notifications.deleteSuccess'))
        await getConceptsModifiedData()
      } else {
        error(t('conceptModifiedCard:notifications.deleteError'))
      }

      closeConfirmation()
    })
  }

  const handleViewPriceDetails = (selectedRow: ConceptModifiedTableModel) => {
    const conceptData = conceptsModifiedData?.find(cm => cm.id === selectedRow.id)
    history.push(NonAuthRoutes.pricingEdit, {
      priceId: conceptData?.price?.id,
      commodity:
        conceptModificationType === ConceptModificationType.CONTRACT
          ? contractData?.commodity
          : conceptData?.price?.commodity,
      priceType: conceptData?.price?.priceType
    })
  }

  // #endregion Handlers Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const fetchProductDetails = async () => {
    setIsFetchingProductDetails(true)
    await Promise.all([
      fetchProductById(productId!).then(response => {
        if (response.success) {
          const newProduct = mapProductModel(response.data)
          setProductDetail(newProduct)
          setBreakdownOptions(getFilteredBreakdownOptions(newProduct.commodity))
        } else {
          error(t('products:notifications.fetchProductError'))
        }
      }),
      fetchProductTypeOptionsByClientType(clientType as EnumClientTypes).then(response => {
        if (response.success) {
          setProductTypeOptions(response.data ?? [])
        } else {
          error(t('products:notifications.FetchProductType.error'))
        }
      })
    ]).finally(() => {
      setIsFetchingProductDetails(false)
    })
  }

  const getConceptsModifiedData = useCallback(async () => {
    // Fetch conceptos a nivel de producto
    if (conceptModificationType === ConceptModificationType.PRODUCT) {
      await getProductConceptPricesByProductID(productId!)
        .then(response => {
          if (response && response.data) {
            const mappedConcepts = mapConcepts(response.data)
            setConceptsModifiedData(mappedConcepts)
          } else {
            error(t('products:notifications.conceptsModified.fetchError'))
          }
        })
        .finally(() => setIsFetching(false))
    } else {
      if (!productContractId) return

      setIsFetching(true)
      await getConceptsModifiedByContractProductID(productContractId)
        .then(response => {
          if (response && response.data) {
            setConceptsModifiedData(response.data)
          } else {
            error(t('products:notifications.conceptsModified.fetchError'))
          }
        })
        .finally(() => setIsFetching(false))
    }
  }, [productContractId])

  useEffect(() => {
    if (productId) {
      fetchProductDetails()
    }
  }, [productId])

  useEffect(() => {
    getConceptsModifiedData()
  }, [getConceptsModifiedData])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageLeftColumn>
        <ProductDetail
          product={productDetail}
          productTypeOptions={productTypeOptions}
          contractData={contractData}
          isLoading={isFetchingProductDetails}
        />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('products:components.productCustomize.header.title')}>
          <Button onClick={handleBackButtonClick} size="small" color="primary">
            {t('products:components.productCustomize.mainActions.back')}
          </Button>
        </PageHeader>
        <Grid container direction="column" spacing={2} className={classes.contentWidthFix}>
          <Grid item xs={12} className={classes.tableScroll}>
            <ConceptModifiedCard
              data={conceptsModifiedData}
              isLoading={isFetching}
              onAddConceptModified={handleOpenDialog}
              onEditConceptModified={handleEditConceptModified}
              onDeleteConceptModified={handleDeleteConceptModified}
              onViewPriceDetails={handleViewPriceDetails}
              conceptModificationType={conceptModificationType}
            />
          </Grid>
          {/*
            Se oculta por Task #77775
           <Grid item>
            <ConceptCard
              filterByRelationType={EnumTipoRelacionProductoConcepto.Obligatorio}
              productDetail={productDetail}
              clientType={clientType as EnumClientTypes}
              isFetching={isFetchingProductDetails}
            />
          </Grid> */}
        </Grid>
      </PageCenterColumn>
      <SimpleDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        title={t('conceptModifiedCard:forms.edit.dialogTitle')}
        footerActions={[
          {
            label: t('globalerisLibrary:form.actions.cancel'),
            color: 'secondary',
            handleActionClick: handleCloseDialog
          },
          {
            label: t('conceptModifiedCard:forms.edit.okBtnLabel'),
            color: 'primary',
            variant: 'contained',
            disabled: !formIsValid || !hasChanges,
            handleActionClick: handleSaveConceptModified
          }
        ]}
      >
        <ConceptModifiedEditForm
          isEditMode={true}
          formData={formData}
          onFormChange={handleFormChange}
          conceptModificationType={conceptModificationType}
          //
          showPriceValidator={conceptModificationType === ConceptModificationType.PRODUCT}
          commodity={productDetail?.commodity as EnumCommodity}
          activeExpression={formData.expression as any}
          activeExpressionIsValid={true}
          activePriceCalculationType={formData.priceCalculationType}
          activeRounding={null}
          onSelectValidExpression={value => {
            setFormData({ ...formData, expression: value })
          }}
          onSelectPriceCalculationType={value => {
            setFormData({ ...formData, priceCalculationType: value })
          }}
          onIsValid={isValid => {
            setFormIsValid(isValid)
            setHasChanges(isValid)
          }}
        />
      </SimpleDialog>
    </Page>
  )
}
